/**
 * Guide engine v3 - launch policy (#1026, epic #1020). Spec R7.
 *
 * Pure decisions for the two launch surfaces:
 *   - AUTO-START: a `required` guide auto-starts for a NEW user (account < 1h old)
 *     unless already completed, and never on a no-auto-start route such as the
 *     invitation page (#1808); the caller asks again on the next route.
 *     Completion suppresses re-start; outside the window
 *     the user re-opens it from the launcher. No tourAvailableFrom cutoff (the
 *     new-user gate alone keeps a later-added required guide from ambushing a
 *     long-time user).
 *   - LAUNCHER (#1010): every guide is openable; ordered newest version first.
 *
 * No DOM, no React, no state - the session controller (wired at cutover, #1029)
 * composes these with the loader + runtime.
 */

const ONE_HOUR_MS = 3600000;

/** True when the account was created within the last hour. */
export const isNewUser = (user, now = Date.now()) => {
  if (!user || !user.createdAt) return false;
  const created = new Date(user.createdAt).getTime();
  if (Number.isNaN(created)) return false;
  return now - created < ONE_HOUR_MS;
};

/**
 * Routes where auto-start waits (#1808). The invitation page is the user's task
 * on arrival: a guide there covers Accept / Decline, and a sandbox guide would
 * swap the active workspace mid-accept. Deferred, not dropped - the caller
 * re-evaluates when the route changes.
 */
const NO_AUTO_START_PREFIXES = ['/invitations/'];

const isNoAutoStartPath = (pathname) =>
  typeof pathname === 'string' && NO_AUTO_START_PREFIXES.some((prefix) => pathname.startsWith(prefix));

/**
 * Should this catalog entry auto-start for the user right now? Required +
 * not-completed (via effectivePriority, which the backend sets to `done` on
 * completion) + new user (R7) + not on a no-auto-start route (#1808).
 * @param {object} entry catalog entry
 * @param {{user: object, now?: number, pathname?: string}} ctx
 * @returns {boolean}
 */
export const shouldAutoStart = (entry, { user, now = Date.now(), pathname } = {}) => {
  if (!entry) return false;
  if (isNoAutoStartPath(pathname)) return false;
  const completed = entry.progress && entry.progress.completed;
  const eff = entry.effectivePriority || (completed ? 'done' : entry.priority);
  if (eff !== 'required') return false;
  return isNewUser(user, now);
};

/**
 * The auto-start set: every entry that should auto-start, in catalog `order`.
 * (Today only the intro qualifies, but the order keeps it deterministic.)
 * @param {object[]} catalog
 * @param {{user: object, now?: number, pathname?: string}} ctx
 * @returns {object[]}
 */
export const pickAutoStart = (catalog, ctx = {}) =>
  (catalog || [])
    .filter((entry) => shouldAutoStart(entry, ctx))
    .sort((a, b) => (a.order || 0) - (b.order || 0));

/**
 * Launcher list (#1010): every guide, newest version first. Does not mutate input.
 * @param {object[]} catalog
 * @returns {object[]}
 */
export const sortForLauncher = (catalog) =>
  [...(catalog || [])].sort((a, b) => String(b.version || '').localeCompare(String(a.version || '')));
